import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Form, Button, Card, Modal, Spinner } from 'react-bootstrap';
import api from '../lib/api';

const FileUploader = () => {
  const [type, setType] = useState('TEXT');
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [urlMeta, setUrlMeta] = useState(null);
  const [urlMaxPages, setUrlMaxPages] = useState(8);
  const [loadingUrlMeta, setLoadingUrlMeta] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMeta, setSuccessMeta] = useState({ type: '', title: '' });
  const [error, setError] = useState('');
  const textRef = useRef(null);

  const nodeToMD = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.nodeValue;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    const el = node;
    const name = el.nodeName.toLowerCase();
    const childrenMD = () => Array.from(el.childNodes).map(nodeToMD).join('');
    switch (name) {
      case 'br':
        return '\n';
      case 'p':
        return `${childrenMD()}\n\n`;
      case 'strong':
      case 'b':
        return `**${childrenMD()}**`;
      case 'em':
      case 'i':
        return `*${childrenMD()}*`;
      case 'a': {
        const href = el.getAttribute('href') || '';
        const label = childrenMD() || href;
        return `[${label}](${href})`;
      }
      case 'ul': {
        const items = Array.from(el.children)
          .filter((li) => li.nodeName.toLowerCase() === 'li')
          .map((li) => `- ${nodeToMD(li).trim()}`)
          .join('\n');
        return `${items}\n\n`;
      }
      case 'ol': {
        let idx = 1;
        const items = Array.from(el.children)
          .filter((li) => li.nodeName.toLowerCase() === 'li')
          .map((li) => `${idx++}. ${nodeToMD(li).trim()}`)
          .join('\n');
        return `${items}\n\n`;
      }
      case 'li': {
        const parts = [];
        Array.from(el.childNodes).forEach((child) => {
          const n = child.nodeName.toLowerCase();
          if (n === 'ul' || n === 'ol') {
            parts.push(`\n${nodeToMD(child).trimEnd()}`);
          } else {
            parts.push(nodeToMD(child));
          }
        });
        return parts.join('').trim();
      }
      case 'blockquote':
        return childrenMD()
          .split('\n')
          .map((l) => (l.trim() ? `> ${l}` : l))
          .join('\n') + '\n\n';
      case 'div':
      case 'section':
      case 'span':
        return childrenMD();
      case 'code':
        return '`' + childrenMD().replace(/\n/g, ' ') + '`';
      case 'pre': {
        const code = el.textContent || '';
        return '```\n' + code.replace(/\n+$/, '') + '\n```\n\n';
      }
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6':
        return `${childrenMD().trim()}\n\n`;
      default:
        return childrenMD();
    }
  };

  const htmlToMarkdown = (html) => {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      let md = nodeToMD(doc.body);
      md = md.replace(/^[\t ]*[•·]\s*/gm, '- ');
      md = md.replace(/\n{3,}/g, '\n\n');
      return md.trim();
    } catch {
      return '';
    }
  };

  const insertAtCursor = (textarea, insert) => {
    const start = textarea.selectionStart ?? textarea.value.length;
    const end = textarea.selectionEnd ?? textarea.value.length;
    const before = textarea.value.slice(0, start);
    const after = textarea.value.slice(end);
    const next = `${before}${insert}${after}`;
    return { next, caret: start + insert.length };
  };

  const autoResize = useCallback(() => {
    const el = textRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 420) + 'px';
  }, []);
  useEffect(() => { autoResize(); }, [text, autoResize]);
  useEffect(() => {
    setUrlMeta(null);
  }, [type]);

  const onPasteText = useCallback((e) => {
    if (!textRef.current) return;
    const html = e.clipboardData?.getData('text/html');
    const plain = e.clipboardData?.getData('text/plain') || '';
    if (html) {
      e.preventDefault();
      const md = htmlToMarkdown(html) || plain;
      const { next, caret } = insertAtCursor(textRef.current, md);
      setText(next);
      requestAnimationFrame(() => {
        textRef.current.selectionStart = textRef.current.selectionEnd = caret;
        autoResize();
      });
    } else {
      e.preventDefault();
      const normalized = plain.replace(/^[\t ]*[•·]\s*/gm, '- ');
      const { next, caret } = insertAtCursor(textRef.current, normalized);
      setText(next);
      requestAnimationFrame(() => {
        textRef.current.selectionStart = textRef.current.selectionEnd = caret;
        autoResize();
      });
    }
  }, [autoResize]);

  const resetFields = () => {
    setTitle('');
    setFile(null);
    setText('');
    setUrl('');
    setUrlMeta(null);
  };

  const fetchUrlMetadata = async (inputUrl) => {
    if (!inputUrl?.trim()) return;
    setLoadingUrlMeta(true);
    try {
      const res = await api.post('/upload/url-metadata/', { url: inputUrl.trim() });
      setUrlMeta(res?.data || null);
      setError('');
    } catch (err) {
      setUrlMeta(null);
      setError(err?.response?.data?.error || 'Unable to fetch URL details.');
    } finally {
      setLoadingUrlMeta(false);
    }
  };

  const buildUploadErrorMessage = (err) => {
    const data = err?.response?.data || {};
    const error = (data?.error || '').toString().trim();
    const detail = (data?.detail || '').toString().trim();
    const hint = (data?.hint || '').toString().trim();
    const backendReason = (data?.backend_reason || '').toString().trim();

    const lines = [];
    if (error) lines.push(error);
    if (detail) lines.push(`Details: ${detail}`);
    if (hint) lines.push(`Fix: ${hint}`);
    if (backendReason) lines.push(`Backend: ${backendReason}`);

    return lines.length ? lines.join('\n') : 'Upload failed. Please try again.';
  };

  const upload = async (e) => {
    e.preventDefault();
    setError('');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('type', type);
      formData.append('title', title);
      if (type === 'TEXT') {
        formData.append('text', text);
        formData.append('format', 'MARKDOWN');
        formData.append('content_type', 'text/markdown');
      }
      if (type === 'URL') {
        formData.append('url', url);
        formData.append('max_pages', String(urlMaxPages || 8));
      }
      if (type === 'PDF' || type === 'CSV') formData.append('file', file);
      await api.post('/upload/', formData);
      setSuccessMeta({ type, title });
      setShowSuccess(true);
      resetFields();
    } catch (err) {
      setError(buildUploadErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const disableSubmit =
    uploading ||
    !title.trim() ||
    (type === 'TEXT' && !text.trim()) ||
    (type === 'URL' && !url.trim()) ||
    ((type === 'PDF' || type === 'CSV') && !file);

  return (
    <>
      <Card className="p-3 shadow-sm">
        <h5>Upload Training Source</h5>
        <Form onSubmit={upload}>
          <Form.Group className="mb-2">
            <Form.Label>Type</Form.Label>
            <Form.Select
              value={type}
              onChange={(e) => setType(e.target.value)}
              style={{ cursor: 'pointer' }}
            >
              <option value="TEXT">Text</option>
              <option value="URL">Website URL</option>
              <option value="PDF">PDF File</option>
              <option value="CSV">CSV File</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Title</Form.Label>
            <Form.Control
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder="e.g., Product FAQs, Onboarding Guide"
            />
          </Form.Group>
          {type === 'TEXT' && (
            <Form.Group className="mb-2">
              <Form.Label>Text</Form.Label>
              <Form.Control
                as="textarea"
                ref={textRef}
                rows={6}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onPaste={onPasteText}
                placeholder="- Point one\n- Point two\n1. First\n2. Second"
                style={{
                  whiteSpace: 'pre-wrap',
                  overflow: 'hidden',
                  resize: 'none',
                  lineHeight: '1.5',
                }}
                required
              />
            </Form.Group>
          )}
          {type === 'URL' && (
            <Form.Group className="mb-2">
              <Form.Label>URL</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="url"
                  inputMode="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onBlur={() => fetchUrlMetadata(url)}
                  placeholder="https://example.com/docs"
                  required
                />
                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={() => fetchUrlMetadata(url)}
                  disabled={!url.trim() || loadingUrlMeta}
                >
                  {loadingUrlMeta ? 'Checking…' : 'Check URL'}
                </Button>
              </div>
              {urlMeta ? (
                <div className="border rounded p-2 mt-2 bg-light">
                  <div><strong>Page title:</strong> {urlMeta.title || 'N/A'}</div>
                  <div><strong>Description:</strong> {urlMeta.description || 'N/A'}</div>
                  <div><strong>Estimated words:</strong> {urlMeta.word_count ?? 0}</div>
                  {Array.isArray(urlMeta.sample_links) && urlMeta.sample_links.length ? (
                    <div className="mt-2">
                      <div><strong>Sample pages to scrape:</strong></div>
                      <ul className="mb-0 ps-3">
                        {urlMeta.sample_links.slice(0, 3).map((link) => (
                          <li key={link} className="small text-break">{link}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : null}
              <div className="mt-2">
                <Form.Label className="small mb-1">Max pages to scrape</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={20}
                  value={urlMaxPages}
                  onChange={(e) => setUrlMaxPages(Number(e.target.value) || 8)}
                />
              </div>
            </Form.Group>
          )}
          {(type === 'PDF' || type === 'CSV') && (
            <Form.Group className="mb-2">
              <Form.Label>File Upload</Form.Label>
              <Form.Control
                type="file"
                onChange={(e) =>
                  setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)
                }
                accept={type === 'PDF' ? 'application/pdf' : '.csv,text/csv'}
                required
              />
            </Form.Group>
          )}
          {error && (
            <div className="text-danger mb-2" role="alert">
              <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{error}</pre>
            </div>
          )}
          <Button
            variant="primary"
            type="submit"
            className="w-100 shadow-sm admin-action-btn"
            disabled={disableSubmit}
            style={{ cursor: 'pointer' }}
          >
            {uploading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" /> Uploading…
              </>
            ) : (
              'Upload'
            )}
          </Button>
        </Form>
      </Card>
      <Modal
        show={showSuccess}
        onHide={() => setShowSuccess(false)}
        centered
        backdrop="static"
        keyboard
      >
        <Modal.Header closeButton>
          <Modal.Title>Upload Successful</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-1">
            <strong>Title:</strong> {successMeta.title || 'Untitled'}
          </p>
          <p className="mb-3">
            <strong>Type:</strong> {successMeta.type}
          </p>
          <p className="mb-0">
            Your training source has been uploaded successfully. You can close this dialog to continue adding more sources.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => setShowSuccess(false)}
            className="shadow-sm admin-action-btn"
            style={{ cursor: 'pointer' }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FileUploader;
